import { apiQuery } from '../';

export const outreachCandidateApi = apiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      getCandidates: builder.query({
        query: ({ params }) => {
          const { outreachId } = params;
          return {
            method: 'get',
            url: `/outreaches/${outreachId}/candidates`,
            params: { page: 1 },
          };
        },
        providesTags: ['GetCandidates'],
      }),

      getOutreachCandidates: builder.query({
        query: ({ page, params, searchTerm, sort, filter }) => {
          const { outreachId } = params;

          if (searchTerm) {
            if (searchTerm?.length < 2) return;
            return {
              method: 'get',
              url: `/outreaches/${outreachId}/candidates`,
              params: {
                page: 1,
                items: 50,
                q: searchTerm,
              },
            };
          }

          const qParams = { page, sort_by: {}, filter_by: {} };

          const { element = null, order = null } = sort;
          const { stages = [], tags = [], sourcedBy = [], step = [] } = filter;
          if (element && order) {
            qParams['sort_by'][element] = order;
          }

          if (stages?.length) {
            qParams['filter_by']['stages'] = stages;
          }

          if (tags?.length) {
            const tagsId = tags?.map(({ id }) => id);
            qParams['filter_by']['candidate_tags'] = tagsId;
          }

          if (sourcedBy?.length) {
            qParams['filter_by']['sourced_by'] = sourcedBy;
          }

          if (step?.length) {
            const stepIds = step.map(({ id }) => id);
            qParams['filter_by']['step_id'] = stepIds;
          } else {
            qParams['filter_by']['step_id'] = [];
          }

          return {
            method: 'get',
            url: `/outreaches/${outreachId}/candidates`,
            params: qParams,
          };
        },
        providesTags: ['GetOutreachCandidates'],
      }),

      pauseCandidate: builder.mutation({
        query: ({ outreachId, candidates }) => {
          return {
            url: `/outreaches/${outreachId}/pause_candidate`,
            method: 'put',
            data: { candidates },
          };
        },
        invalidatesTags: ['GetOutreachCandidates'],
      }),

      resumeCandidate: builder.mutation({
        query: ({ outreachId, candidates }) => {
          return {
            url: `/outreaches/${outreachId}/resume_candidate`,
            method: 'put',
            data: { candidates },
          };
        },
        invalidatesTags: ['GetOutreachCandidates'],
      }),

      moveStep: builder.mutation({
        query: ({ outreachId, candidates, step }) => {
          return {
            url: `/outreaches/${outreachId}/candidate_step`,
            method: 'put',
            params: { candidates, to_step: step },
          };
        },
        invalidatesTags: (result) => {
          if (result?.data) {
            return [{ type: 'GetOutreachCandidates' }];
          }
          return [];
        },
      }),

      restartOutreach: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ outreachId, candidates }) => {
          return {
            url: `/outreaches/${outreachId}/restart_failed`,
            method: 'put',
            data: { candidates },
          };
        },
        invalidatesTags: (data) => {
          // Invalidate, if atleast one failed candidate is restarted.
          const canInvalidate = data?.find(
            (item) => item?.errors?.length === 0
          );
          if (canInvalidate) {
            return [{ type: 'GetOutreachCandidates' }];
          }
          return [];
        },
      }),

      moveFlowStage: builder.mutation({
        query: ({ outreachId, candidateId, stage }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/${candidateId}/stage`,
            method: 'put',
            data: { stage },
          };
        },
        invalidatesTags: ['GetOutreachCandidates'],
      }),

      rejectFlowStage: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({
          outreachId,
          candidateId,
          stage,
          reasonId,
          note,
          ats_enabled,
        }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/${candidateId}/stage`,
            method: 'put',
            data: ats_enabled
              ? {
                  stage,
                  not_interested_ats_rejection_reason_id: reasonId?.id,
                  rejection_note: note,
                }
              : {
                  stage,
                  ats_archive_reason_id: reasonId?.id,
                  archive_note: note,
                },
          };
        },
        invalidatesTags: (result) => {
          if (result?.[0]?.errors?.length > 0 || result?.[0]?.errors?.contact) {
            return [];
          }
          return [{ type: 'GetOutreachCandidates' }];
        },
      }),

      stepSchedule: builder.query({
        transformResponse: (response) => response.data,
        query: ({ params }) => {
          const { outreachId, candidateId } = params;
          return {
            method: 'get',
            url: `/outreaches/${outreachId}/candidates/${candidateId}/step_schedule`,
          };
        },
      }),
    };
  },
  overrideExisting: true,
});
export const {
  useGetOutreachCandidatesQuery,
  useGetCandidatesQuery,
  useMoveStageMutation,
  useMoveStepMutation,
  useMoveOutreachMutation,
  usePauseCandidateMutation,
  useResumeCandidateMutation,
  useRestartOutreachMutation,
  useStepScheduleQuery,
  useMoveFlowStageMutation,
  useRejectFlowStageMutation,
} = outreachCandidateApi;
